import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext.js';
import { homes, type AuditEntry } from '../../lib/api.js';

function formatAction(action: string): string {
  return action
    .split('.')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' · ');
}

function actorLabel(e: AuditEntry): string {
  if (e.actorDisplayName) return e.actorDisplayName;
  if (e.actorEmail) return e.actorEmail;
  return 'System';
}

export default function AuditSettingsPage() {
  const { currentHomeId } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentHomeId) return;
    setLoading(true);
    setError(null);
    homes
      .audit(currentHomeId, 50)
      .then((r) => {
        setEntries(r.entries);
        setNextBefore(r.nextBefore);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'load-failed'))
      .finally(() => setLoading(false));
  }, [currentHomeId]);

  const loadMore = async () => {
    if (!currentHomeId || !nextBefore) return;
    setLoading(true);
    try {
      const r = await homes.audit(currentHomeId, 50, nextBefore);
      setEntries((prev) => [...prev, ...r.entries]);
      setNextBefore(r.nextBefore);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load-failed');
    } finally {
      setLoading(false);
    }
  };

  const visible = useMemo(() => {
    if (!filter) return entries;
    const q = filter.toLowerCase();
    return entries.filter(
      (e) =>
        e.action.toLowerCase().includes(q) ||
        actorLabel(e).toLowerCase().includes(q) ||
        (e.target ?? '').toLowerCase().includes(q),
    );
  }, [entries, filter]);

  return (
    <div className="max-w-4xl mx-auto p-4 flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--ac-text)' }}>
          Audit log
        </h1>
        <p className="text-sm" style={{ color: 'var(--ac-text-muted)' }}>
          Who did what in this home. Newest first.
        </p>
      </div>

      <input
        type="search"
        placeholder="Filter by action, actor, or target…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="p-2 rounded-lg border text-sm"
        style={{
          backgroundColor: 'var(--ac-surface)',
          borderColor: 'var(--ac-border)',
          color: 'var(--ac-text)',
        }}
      />

      {error && (
        <div
          className="p-3 rounded-lg text-sm"
          style={{ backgroundColor: 'var(--ac-surface)', color: 'var(--ac-danger, #b91c1c)' }}
        >
          {error}
        </div>
      )}

      {!loading && entries.length === 0 && !error && (
        <div
          className="p-6 rounded-lg border text-sm text-center"
          style={{
            backgroundColor: 'var(--ac-surface)',
            borderColor: 'var(--ac-border)',
            color: 'var(--ac-text-muted)',
          }}
        >
          No events recorded yet.
        </div>
      )}

      {visible.length > 0 && (
        <div
          className="rounded-lg border overflow-hidden"
          style={{ borderColor: 'var(--ac-border)' }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--ac-surface)' }}>
                <th className="text-left p-2" style={{ color: 'var(--ac-text-muted)' }}>
                  When
                </th>
                <th className="text-left p-2" style={{ color: 'var(--ac-text-muted)' }}>
                  Action
                </th>
                <th className="text-left p-2" style={{ color: 'var(--ac-text-muted)' }}>
                  Actor
                </th>
                <th className="text-left p-2" style={{ color: 'var(--ac-text-muted)' }}>
                  Target
                </th>
                <th className="text-left p-2" style={{ color: 'var(--ac-text-muted)' }}>
                  OK
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => (
                <tr
                  key={e.id}
                  className="border-t"
                  style={{
                    borderColor: 'var(--ac-border)',
                    color: 'var(--ac-text)',
                  }}
                >
                  <td className="p-2 whitespace-nowrap" style={{ color: 'var(--ac-text-muted)' }}>
                    {new Date(e.createdAt).toLocaleString()}
                  </td>
                  <td className="p-2">{formatAction(e.action)}</td>
                  <td className="p-2">{actorLabel(e)}</td>
                  <td className="p-2 font-mono text-xs" style={{ color: 'var(--ac-text-muted)' }}>
                    {e.target ?? '—'}
                  </td>
                  <td className="p-2">
                    <span aria-label={e.success ? 'success' : 'failure'}>
                      {e.success ? '✓' : '✗'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nextBefore && (
        <button
          type="button"
          onClick={() => void loadMore()}
          disabled={loading}
          className="self-center px-4 py-2 rounded-lg border text-sm"
          style={{
            backgroundColor: 'var(--ac-surface)',
            borderColor: 'var(--ac-border)',
            color: 'var(--ac-text)',
          }}
        >
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}
